import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeftIcon, ChevronRightIcon, MinusIcon } from "lucide-react";
import { type SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { z } from "zod";
import { SubmitButton } from "@/components/buttons/submit-button";
import { LoadingModal } from "@/components/modals/loading-modal";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Timeline,
  TimelineContent,
  TimelineHeader,
  TimelineIndicator,
  TimelineItem,
  TimelineSeparator,
  TimelineTitle,
} from "@/components/ui/timeline";
import { useSession } from "@/hooks/use-session";
import { formatCurrency } from "@/lib/utils";
import {
  useGetEasiRechargeOptionsQuery,
  useLazyCheckCustomerQuery,
} from "@/redux/services/order-portal";
import { setEasiRecharge } from "@/redux/slices/easi-recharge";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { selectPackageSchema } from "@/shared/schemas/easi-recharge/select-package";

export const SelectPackageForm = ({ className }: { className?: string }) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const state = useAppSelector((prevState) => prevState.easiRecharge);
  const dispatch = useAppDispatch();
  const { data: options, isLoading: isLoadingOptions } =
    useGetEasiRechargeOptionsQuery({
      loginToken: session?.token ?? "",
    });
  const [checkCustomer, { currentData: customer }] =
    useLazyCheckCustomerQuery();

  const form = useForm<z.infer<typeof selectPackageSchema>>({
    resolver: zodResolver(selectPackageSchema),
    defaultValues: {
      mobileNumber: state.mobileNumber ?? "",
      package: {
        id: state.package.id && state.package.id !== 0 ? state.package.id : 0,
        code: state.package.code ?? "",
        price: state.package.price ?? "",
      },
    },
  });

  const mobileNumber = useWatch({
    control: form.control,
    name: "mobileNumber",
  });

  async function handleCheckCustomer() {
    try {
      const response = await checkCustomer({
        msisdn: mobileNumber,
      });
      if (response.error) {
        throw new Error(
          (response.error as APIErrorResponse).data.message ??
            "Failed to check customer"
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error has occursed"
      );
    }
  }

  const onSubmit: SubmitHandler<z.infer<typeof selectPackageSchema>> = (
    data
  ) => {
    try {
      dispatch(
        setEasiRecharge({
          mobileNumber: data.mobileNumber,
          package: data.package,
        })
      );

      navigate("/easi-recharge/summary");

      form.reset();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  };

  return (
    <>
      {isLoadingOptions && <LoadingModal isOpen={isLoadingOptions} />}

      <Form {...form}>
        <form className={className} onSubmit={form.handleSubmit(onSubmit)}>
          <Timeline defaultValue={1}>
            <TimelineItem step={1}>
              <TimelineHeader>
                <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
                <TimelineTitle className="-mt-1">
                  {t("easi-recharge-customer-information.titles.mobile-number")}
                </TimelineTitle>
                <TimelineIndicator className="flex size-6 items-center justify-center">
                  <MinusIcon className="size-4" />
                </TimelineIndicator>
              </TimelineHeader>
              <TimelineContent className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="mobileNumber"
                  render={({ field }) => (
                    <FormItem className="max-w-xs">
                      <FormLabel className="sr-only">
                        {t("labels.mobile-number")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={`${t("labels.mobile-number")}*`}
                          type="tel"
                          {...field}
                          onBlur={handleCheckCustomer}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TimelineContent>
            </TimelineItem>
            {customer && customer.type === "PREPAID" && customer.is_valid && (
              <TimelineItem step={2}>
                <TimelineHeader>
                  <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
                  <TimelineTitle className="-mt-1">
                    {t(
                      "easi-recharge-customer-information.titles.choose-package"
                    )}
                  </TimelineTitle>
                  <TimelineIndicator className="flex size-6 items-center justify-center">
                    <MinusIcon className="size-4" />
                  </TimelineIndicator>
                </TimelineHeader>
                <TimelineContent className="flex flex-col gap-4">
                  <FormField
                    control={form.control}
                    name="package.code"
                    render={({ field }) => (
                      <FormItem className="max-w-xs">
                        <FormLabel className="sr-only">Package</FormLabel>
                        <Select
                          defaultValue={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);

                            const selectedPackage = options?.find(
                              (option) => option.code === value
                            );
                            form.setValue(
                              "package.id",
                              selectedPackage?.id ?? 0
                            );
                            form.setValue(
                              "package.price",
                              selectedPackage?.price ?? ""
                            );
                          }}
                          {...field}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={`${t("placeholders.package")}*`}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {options?.map((option) => {
                              const price = Number(option.price);

                              return (
                                <SelectItem key={option.id} value={option.code}>
                                  {`Easiload ${formatCurrency(price, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })} | ${formatCurrency(price)}`}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TimelineContent>
              </TimelineItem>
            )}
          </Timeline>

          <Separator />

          <div className="flex items-center justify-between gap-6">
            <Button
              onClick={() => navigate(-1)}
              type="button"
              variant="outline"
            >
              <ChevronLeftIcon />
              <span>{t("labels.back")}</span>
            </Button>
            {form.formState.isValid &&
            customer &&
            customer.type === "PREPAID" ? (
              <SubmitButton
                className="rounded-md"
                isSubmitting={form.formState.isSubmitting}
              >
                <span>{t("labels.next")}</span>
                <ChevronRightIcon />
              </SubmitButton>
            ) : (
              <Button onClick={handleCheckCustomer} type="button">
                <span>{t("labels.next")}</span>
                <ChevronRightIcon />
              </Button>
            )}
          </div>
        </form>
      </Form>
    </>
  );
};
